import { HowToAnswer } from "./HowToAnswer";
import { useMemo, useState } from "react";
import { Check } from "lucide-react";

/**
 * MultiSelect — "check all that apply", with a soft maximum reminder but
 * no hard cap. Learners can tick zero, all, or anything in between.
 * The screen decides how to score the pattern (usually a ratio and a
 * per-facet flag list).
 */

export interface MultiSelectItem {
  id: string;
  label: string;
  hint?: string;
}

export interface MultiSelectProps {
  items: MultiSelectItem[];
  onComplete: (selectedIds: string[]) => void;
  ctaLabel?: string;
  /** If the learner selects 0, the CTA still enables — sometimes "none of these" is honest. */
  allowEmpty?: boolean;
}

export function MultiSelect({
  items, onComplete, ctaLabel = "Continue", allowEmpty = true,
}: MultiSelectProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const list = useMemo(() => Array.from(selected), [selected]);
  const canProceed = allowEmpty || selected.size > 0;

  function toggle(id: string) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return (
    <div className="space-y-4">
      <HowToAnswer>Tap every option that applies — as many or as few as you like. Tap again to remove.</HowToAnswer>
      <div className="grid grid-cols-1 gap-2">
        {items.map((it) => {
          const active = selected.has(it.id);
          return (
            <button
              key={it.id}
              onClick={() => toggle(it.id)}
              aria-pressed={active}
              className={[
                "text-left rounded-2xl border p-4 min-h-[56px] flex items-start gap-3 transition",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                active
                  ? "border-primary bg-primary/10"
                  : "border-border bg-card hover:border-primary/50",
              ].join(" ")}
            >
              <span
                className={[
                  "shrink-0 mt-0.5 h-6 w-6 rounded-md grid place-items-center border",
                  active ? "bg-primary border-primary text-primary-foreground" : "border-border",
                ].join(" ")}
                aria-hidden
              >
                {active && <Check className="h-4 w-4" />}
              </span>
              <div className="flex-1">
                <div className="text-base">{it.label}</div>
                {it.hint && <div className="text-xs text-muted-foreground mt-1">{it.hint}</div>}
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between pt-2">
        <div className="text-xs text-muted-foreground tabular-nums">
          {selected.size} selected
        </div>
        <button
          onClick={() => canProceed && onComplete(list)}
          disabled={!canProceed}
          className="min-h-[48px] px-6 rounded-2xl bg-primary text-primary-foreground font-medium
            disabled:opacity-40 disabled:pointer-events-none
            focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {ctaLabel}
        </button>
      </div>
    </div>
  );
}
